import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, UserX } from 'lucide-react';
import { api } from '@/services/api';
import type { AccountClosureStatus } from '@/services/api';
import { formatNumericDate } from '@/i18n/intl';
import { PageHead } from '@/components/editorial';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

type Notice = 'cancelled' | 'alreadyScheduled' | null;

/**
 * /account/close (#2390). Authenticated but outside the re-consent gate, so a
 * blocked user can reach it; for the same reason it renders no back link into
 * the app (`parentFor` returns null here, §4.6).
 *
 * The closure state is read back from the server on load. It used to live in
 * this component's state only, so after a sign-out nothing in the app showed
 * a pending closure and the page offered "Schedule" again. Scheduling goes
 * through a confirm step, and a scheduled closure can be cancelled here
 * (D-2390, in-app cancel).
 */
export function AccountClosePage() {
  const { t } = useTranslation('legal');
  const [status, setStatus] = useState<AccountClosureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    let live = true;
    api.account.closeStatus()
      .then((r) => {
        if (!live) return;
        if (r.status === 'success' && r.data) setStatus(r.data);
        else setLoadFailed(true);
      })
      .catch(() => { if (live) setLoadFailed(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const run = async (
    call: () => Promise<{ status: string; data?: Partial<AccountClosureStatus> }>,
    onDone: (s: AccountClosureStatus) => void,
  ) => {
    setBusy(true);
    setActionFailed(false);
    setNotice(null);
    try {
      const r = await call();
      if (r.status === 'success' && r.data) {
        onDone({
          grace_days: r.data.grace_days ?? 30,
          closure_requested_at: r.data.closure_requested_at ?? null,
          closure_scheduled_at: r.data.closure_scheduled_at ?? null,
          already_scheduled: r.data.already_scheduled,
        });
      }
      else setActionFailed(true);
    } catch {
      setActionFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const onSchedule = () => run(api.account.close, (s) => {
    setStatus(s);
    setLoadFailed(false);
    if (s.already_scheduled) setNotice('alreadyScheduled');
  });

  const onCancel = () => run(api.account.cancelClose, (s) => {
    setStatus(s);
    setNotice('cancelled');
  });

  const scheduledAt = status?.closure_scheduled_at ?? null;
  const graceDays = status?.grace_days ?? 30;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-stevens-ink-3" aria-hidden />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHead
        density="compact"
        eyebrow={t('accountClose.eyebrow')}
        icon={UserX}
        title={scheduledAt ? t('accountClose.scheduledTitle') : t('accountClose.title')}
        lede={
          <>
            {scheduledAt
              ? t('accountClose.scheduledBody', { date: formatNumericDate(scheduledAt) })
              : t('accountClose.body')}
            {/* #2390: the page's edge cases are defaults (D-2390 rules the cancel only). */}
            {' '}<PendingDecision id="account-closure-page-details" side="bottom" />
          </>
        }
      />

      <div role="status" className="space-y-2">
        {notice === 'cancelled' ? (
          <p className="text-body-sm text-foreground">{t('accountClose.cancelledNotice')}</p>
        ) : null}
        {notice === 'alreadyScheduled' ? (
          <p className="text-body-sm text-foreground">{t('accountClose.alreadyScheduledNotice')}</p>
        ) : null}
        {loadFailed && !scheduledAt ? (
          <p className="text-body-sm text-destructive">{t('accountClose.loadFailed')}</p>
        ) : null}
        {actionFailed ? (
          <p className="text-body-sm text-destructive">{t('accountClose.actionFailed')}</p>
        ) : null}
      </div>

      {scheduledAt ? (
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin me-2" aria-hidden /> : null}
          {t('accountClose.cancelAction')}
        </Button>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin me-2" aria-hidden /> : null}
              {t('accountClose.action')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('accountClose.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('accountClose.confirmBody', { days: graceDays })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('accountClose.confirmDismiss')}</AlertDialogCancel>
              <AlertDialogAction onClick={onSchedule}>{t('accountClose.confirmAction')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
